import Axios from "../../../lib/Axios";

/**
 * Reported Hotspot API Helper
 * 
 * This file contains all API calls related to Reported Hotspot.
 */

/**
 * Fetch all reported hotspots with filters
 * @param {Object} filters - Filter parameters
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @param {string} filters.keyword - Search keyword
 * @returns {Promise} API response - Returns array of reported hotspot objects
 */
export const getReportedHotspotList = async (filters = {}) => {
  const {
    limit = 50,
    page = 1,
    keyword,
    is_suspended,
  } = filters;

  // Build query parameters object
  const params = {
    limit: limit.toString(),
    page: page.toString(),
  };
  
  if (keyword) params.keyword = keyword;
  if (is_suspended !== undefined && is_suspended !== "") {
    // Convert string "true"/"false" to boolean, or keep boolean as is
    if (is_suspended === "true" || is_suspended === true) {
      params.is_suspended = true;
    } else if (is_suspended === "false" || is_suspended === false) {
      params.is_suspended = false;
    }
  }

  console.log("API Params:", params);
  console.log("is_suspended from filters:", is_suspended);

  const result = await Axios.get("admin/hotspot-reports", { params });
  const data = result?.data?.result?.hotspot_reports || [];
  const pagination = result?.data?.result?.pagination || {};
  return {
    data,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Get reports for a specific tag
 * @param {number} tag_id - The tag ID
 * @param {Object} filters - Filter parameters
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @returns {Promise} API response - Returns reports array and tag info
 */
export const getTagReports = async (tag_id, filters = {}) => {
  if (!tag_id) {
    throw new Error("tag_id is required");
  }

  const {
    limit = 50,
    page = 1,
  } = filters;

  const params = {
    limit: limit.toString(),
    page: page.toString(),
  };

  // GET /admin/hotspot-reports/{tag_id}
  const result = await Axios.get(`admin/hotspot-reports/${tag_id}`, { params });
  const reports = result?.data?.result?.reports || [];
  const tagInfo = result?.data?.result || {};
  const pagination = result?.data?.result?.pagination || {};

  return {
    tag_id: tagInfo.tag_id,
    reports,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Update report state and admin note
 * @param {Object} data - Update data
 * @param {Array<string>} data.report_ids - Array of report IDs to update
 * @param {string} data.state - State: 'none', 'raised', 'in progress', or 'resolved'
 * @param {string} data.adminNote - Admin note text
 * @returns {Promise} API response
 */
export const updateReport = async (data) => {
  const { report_ids, state, adminNote } = data;
  
  if (!report_ids || !Array.isArray(report_ids) || report_ids.length === 0) {
    throw new Error("report_ids array is required");
  }

  // PUT /admin/hotspot-reports
  const payload = {
    report_ids,
    state: state, // State is required
  };

  // Only include adminNote if provided
  if (adminNote) {
    payload.adminNote = adminNote;
  }

  const result = await Axios.put("admin/hotspot-reports", payload);
  
  return result;
};

/**
 * Get broadcasts for a specific tag
 * @param {number} tag_id - The tag ID
 * @param {Object} filters - Filter parameters
 * @param {number} filters.limit - Pagination limit
 * @param {number} filters.page - Pagination page number
 * @param {string} filters.status - Status filter: 'active', 'expired', or 'all' (if 'all', don't send)
 * @param {string} filters.dateRange - Date range: 'last_24_hours', 'last_7_days', 'last_30_days'
 * @param {string} filters.startDate - Start date in YYYY-MM-DD format
 * @param {string} filters.endDate - End date in YYYY-MM-DD format
 * @returns {Promise} API response - Returns broadcasts array and pagination
 */
export const getTagBroadcasts = async (tag_id, filters = {}) => {
  if (!tag_id) {
    throw new Error("tag_id is required");
  }

  const {
    limit = 50,
    page = 1,
    status,
    dateRange,
    startDate,
    endDate,
  } = filters;

  const params = {
    tag_id: tag_id.toString(),
    limit: limit.toString(),
    page: page.toString(),
  };

  // Only include status if it's not "all"
  if (status && status !== "all") {
    params.status = status;
  }

  // Only include dateRange if provided and not "custom"
  if (dateRange && dateRange !== "custom") {
    params.dateRange = dateRange;
  }

  // Only include startDate and endDate if provided (for custom date range or when dateRange is "custom")
  if (startDate) {
    params.startDate = startDate;
  }

  if (endDate) {
    params.endDate = endDate;
  }

  // GET /admin/broadcasts
  const result = await Axios.get("admin/broadcasts", { params });
  const broadcasts = result?.data?.result?.broadcasts || [];
  const pagination = result?.data?.result?.pagination || {};

  return {
    broadcasts,
    pagination: {
      currentPage: pagination.currentPage || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages || 1,
      totalRecords: pagination.totalRecords || 0,
    },
  };
};

/**
 * Suspend a hotspot tag
 * @param {Object} data - Suspend data
 * @param {number} data.tag_id - The tag ID to suspend
 * @param {string} data.reason - Reason for suspension
 * @returns {Promise} API response
 */
export const suspendHotspot = async (data) => {
  const { tag_id, reason } = data;
  
  if (!tag_id) {
    throw new Error("tag_id is required");
  }
  if (!reason) {
    throw new Error("reason is required");
  }

  // POST /admin/tags/suspend
  const result = await Axios.post("admin/tags/suspend", {
    tag_id,
    reason,
  });
  
  return result;
};

